import React, { useState, useRef, useEffect } from "react";
import { User, ChevronDown, X } from "lucide-react";

interface User {
  id: string;
  name: string;
  email: string;
}

interface TypeaheadUserInputProps {
  id: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  users: User[];
  loading?: boolean;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export default function TypeaheadUserInput({
  id,
  name,
  value,
  onChange,
  users,
  loading = false,
  placeholder = "Enter email or select user",
  className = "",
  disabled = false,
}: TypeaheadUserInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Initialize input value based on the current value
  useEffect(() => {
    if (value) {
      // Check if value is a user ID
      const selectedUser = users.find((user) => user.id === value);
      if (selectedUser) {
        setInputValue(`${selectedUser.name} (${selectedUser.email})`);
      } else {
        // Assume it's an email
        setInputValue(value);
      }
    } else {
      setInputValue("");
    }
  }, [value, users]);

  // Filter users based on input
  useEffect(() => {
    if (!inputValue.trim()) {
      setFilteredUsers(users.slice(0, 10)); // Show first 10 users when empty
      return;
    }

    const filtered = users
      .filter(
        (user) =>
          user.name.toLowerCase().includes(inputValue.toLowerCase()) ||
          user.email.toLowerCase().includes(inputValue.toLowerCase()),
      )
      .slice(0, 10); // Limit to 10 results

    setFilteredUsers(filtered);
  }, [inputValue, users]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsOpen(true);

    // If it looks like an email, pass it directly
    if (newValue.includes("@")) {
      onChange(newValue);
    } else {
      // Check if it matches a user exactly
      const exactUser = users.find(
        (user) => `${user.name} (${user.email})` === newValue,
      );
      if (exactUser) {
        onChange(exactUser.id);
      } else {
        onChange(newValue);
      }
    }
  };

  const handleUserSelect = (user: User) => {
    setInputValue(`${user.name} (${user.email})`);
    onChange(user.id);
    setIsOpen(false);
  };

  const handleClear = () => {
    setInputValue("");
    onChange("");
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleFocus = () => {
    setIsOpen(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          id={id}
          name={name}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          disabled={disabled || loading}
          className="input pr-16"
          placeholder={loading ? "Loading users..." : placeholder}
          autoComplete="off"
        />

        <div className="absolute inset-y-0 right-0 flex items-center">
          {inputValue && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 text-gray-400 hover:text-gray-600 mr-1"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <div className="p-2 text-gray-400">
            <ChevronDown
              className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
            />
          </div>
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {loading ? (
            <div className="p-3 text-center text-gray-500">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mx-auto mb-2"></div>
              Loading users...
            </div>
          ) : filteredUsers.length > 0 ? (
            <>
              {filteredUsers.map((user) => (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => handleUserSelect(user)}
                  className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2 text-sm"
                >
                  <User className="h-4 w-4 text-gray-400" />
                  <div>
                    <div className="text-gray-900 dark:text-gray-100">
                      {user.name}
                    </div>
                    <div className="text-gray-500 dark:text-gray-400 text-xs">
                      {user.email}
                    </div>
                  </div>
                </button>
              ))}
              {inputValue &&
                inputValue.includes("@") &&
                !users.find((u) => u.email === inputValue) && (
                  <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-600">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                      Custom email:
                    </div>
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {inputValue}
                    </div>
                  </div>
                )}
            </>
          ) : (
            <div className="p-3 text-center text-gray-500 dark:text-gray-400 text-sm">
              {inputValue ? "No users found" : "Start typing to search users"}
              {inputValue && inputValue.includes("@") && (
                <div className="mt-2 text-xs">
                  Will use: <span className="font-medium">{inputValue}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
